import { ref, type Ref } from 'vue'

import type { EditorHistoryCommand } from '@/editor/history/types'
import { UndoRedoStack } from '@/editor/history/undoRedoStack'

/**
 * 编辑器历史网关：统一入栈、撤销、重做；`applying` 期间不入栈。
 */
export class EditorHistoryGateway {
  private readonly stack: UndoRedoStack

  /** 正在执行 undo/redo 内部的文档变更，禁止再次 `execute` */
  private applying = false

  /**
   * 栈变更计数：供 UI `computed` 依赖以刷新 `canUndo` / `canRedo`。
   */
  readonly revision: Ref<number> = ref(0)

  /**
   * @param getMaxUndoDepth - 读取全局最大撤销深度
   */
  constructor(getMaxUndoDepth: () => number) {
    this.stack = new UndoRedoStack(getMaxUndoDepth)
  }

  /**
   * 通知依赖方历史栈已变化。
   */
  private bump(): void {
    this.revision.value += 1
  }

  /**
   * @returns 是否处于 undo/redo 应用过程中
   */
  get isApplying(): boolean {
    return this.applying
  }

  /**
   * @returns 是否可撤销
   */
  canUndo(): boolean {
    return this.stack.canUndo()
  }

  /**
   * @returns 是否可重做
   */
  canRedo(): boolean {
    return this.stack.canRedo()
  }

  /**
   * 记录一条已生效的变更（由调用方先改文档，再传入命令）。
   *
   * @param cmd - 命令实例
   */
  execute(cmd: EditorHistoryCommand): void {
    if (this.applying) return
    this.stack.push(cmd)
    this.bump()
  }

  /**
   * 撤销一步。
   *
   * @returns 是否执行了撤销
   */
  undo(): boolean {
    if (!this.stack.canUndo()) return false
    this.applying = true
    try {
      const ok = this.stack.undo()
      if (ok) this.bump()
      return ok
    } finally {
      this.applying = false
    }
  }

  /**
   * 重做一步。
   *
   * @returns 是否执行了重做
   */
  redo(): boolean {
    if (!this.stack.canRedo()) return false
    this.applying = true
    try {
      const ok = this.stack.redo()
      if (ok) this.bump()
      return ok
    } finally {
      this.applying = false
    }
  }

  /**
   * 清空历史（加载工程、重置项目等）。
   */
  clear(): void {
    this.stack.clear()
    this.bump()
  }
}
